import { ChangeEvent, Component } from "react";
import {
    Button,
    createStyles,
    Paper,
    Theme,
    Typography,
    WithStyles,
    withStyles
} from "@material-ui/core";
import $ from "jquery";
import * as postcode from "postcode-validator";
import ReactGA from "react-ga4";
import { connect } from "react-redux";
import { Redirect } from "react-router";
import { bindActionCreators } from "redux";
import { RedirectTo } from "../../helpers/RedirectTo";
import IUserProfile from "../../models/account/IUserProfile";
import Constants from "../../models/utility/Constants";
import { LoadState } from "../../models/utility/LoadState";
import * as AppConfigStore from "../../store/AppConfigStore";
import FullscreenLoader from "../layout/FullscreenLoader";
import FormErrorWrapper from "../shared/FormErrorWrapper";
import LoaderButton from "../shared/LoaderButton";
import { AppInsightsLogger } from "../shared/logging/AppInsightsLogger";
import AccountDetails from "../shared/registration/AccountDetails";
import ProfileInformation from "../shared/registration/ProfileInformation";
import SectionBanner from "../shared/SectionBanner";
import { IAppDispatch, IAppState } from "./../../store";
import * as UserStore from "./../../store/UserStore";

type ProfileProps = UserStore.IUserStoreState &
    typeof UserStore.actionCreators &
    AppConfigStore.IAppConfigStoreState &
    WithStyles<typeof styles>;

interface IProfileState {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    phoneNumber: string;
    address1: string;
    address2: string;
    country: string;
    province: string;
    city: string;
    postalCode: string;
    hasErrors: boolean;
    errorMessages: string[];
}

const styles = (theme: Theme) =>
    createStyles({
        button: { marginTop: theme.spacing(5) },
        errorMessage: { textAlign: "left" },
        form: {
            maxWidth: 1000,
            padding: theme.spacing(8),
            width: "100%"
        },
        paper: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#231F20"
        }
    });

class Profile extends Component<ProfileProps, IProfileState> {
    constructor(props: ProfileProps) {
        super(props);

        Constants.SetDocumentTitle("My Account");

        this.state = {
            address1: "",
            address2: "",
            city: "",
            confirmPassword: "",
            country: "CA",
            email: "",
            errorMessages: [],
            firstName: "",
            hasErrors: false,
            lastName: "",
            password: "",
            phoneNumber: "",
            postalCode: "",
            province: ""
        };
    }

    public componentWillMount() {
        if (this.props.user) {
            this.props.getMyProfile();
        }
    }

    public componentDidUpdate(
        prevProps: ProfileProps,
        prevState: IProfileState
    ) {
        if (!prevProps.userProfile && this.props.userProfile) {
            const userProfile = this.props.userProfile;
            this.setState({
                address1: userProfile.address1 || "",
                address2: userProfile.address2 || "",
                city: userProfile.city || "",
                confirmPassword: "",
                country: userProfile.country || "CA",
                email: userProfile.email || "",
                errorMessages: [],
                firstName: userProfile.firstName || "",
                hasErrors: false,
                lastName: userProfile.lastName || "",
                password: "",
                phoneNumber: userProfile.phoneNumber || "",
                postalCode: userProfile.postalCode || "",
                province: userProfile.province || ""
            });
        }
        if (
            (this.props.updateUserProfileFailErrors &&
                this.props.updateUserProfileFailErrors.length > 0 &&
                prevProps.updateUserProfileFailErrors !==
                    this.props.updateUserProfileFailErrors) ||
            (this.state.errorMessages &&
                this.state.errorMessages.length > 0 &&
                prevState.errorMessages !== this.state.errorMessages)
        ) {
            // scroll to top if there are new error messages
            $("body, html").animate(
                { scrollTop: 0 },
                Constants.ScrollAnimationLength
            );
        }
    }

    public render() {
        if (this.props.userProfileLoadState !== LoadState.Loaded) {
            return <FullscreenLoader />;
        }

        if (this.props.updateUserProfileSucceed) {
            if (this.props.prevLocation) {
                return <Redirect to={this.props.prevLocation} />;
            } else {
                RedirectTo(
                    Constants.Routes.AuctionList({
                        baseUrl: this.props.salesPageUrl
                    })
                );
            }
        }

        const classes = this.props.classes;
        if (!this.props.userProfile) {
            return (
                <Paper className={classes.paper} elevation={2}>
                    <SectionBanner leftSideText="My Account" />
                    <div className={classes.form}>
                        <Typography>Couldn't load your account.</Typography>
                        <Button
                            className={classes.button}
                            variant="contained"
                            onClick={() => this.props.getMyProfile()}
                        >
                            Retry
                        </Button>
                    </div>
                </Paper>
            );
        }

        const hasErrors =
            this.state.hasErrors || this.props.updateUserProfileFailure;
        const errorMessages = this.state.errorMessages.concat(
            this.props.updateUserProfileFailErrors
        );
        return (
            <Paper className={classes.paper} elevation={2}>
                <SectionBanner leftSideText="My Account" />
                <div className={classes.form}>
                    <form>
                        {hasErrors ? (
                            <FormErrorWrapper
                                errors={errorMessages}
                                errorMessage="The following errors occurred during account update:"
                            />
                        ) : null}
                        <AccountDetails
                            firstName={this.state.firstName}
                            lastName={this.state.lastName}
                            email={this.state.email}
                            isEdit={true}
                            isViewMode={false}
                            allowPasswordReset={true}
                            onChange={this.onChange}
                        />
                        <ProfileInformation
                            phoneNumber={this.state.phoneNumber}
                            address1={this.state.address1}
                            address2={this.state.address2}
                            country={this.state.country}
                            province={this.state.province}
                            city={this.state.city}
                            postalCode={this.state.postalCode}
                            isViewMode={false}
                            onChange={this.onChange}
                        />
                        <div className={classes.button}>
                            <LoaderButton
                                loading={false}
                                type="button"
                                size="large"
                                variant="contained"
                                onClick={this.onSubmit}
                            >
                                Update
                            </LoaderButton>
                        </div>
                    </form>
                </div>
            </Paper>
        );
    }

    private onChange = (event: ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value as string;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.setState({ [name]: value } as any);

        // Reset province on country change
        if (name === "country") {
            this.setState({ province: "" });
        }
    };

    private isASCII = (str: string) => {
        // eslint-disable-next-line no-control-regex
        return /^[\x00-\x7F]*$/.test(str);
    };

    private validateForm = () => {
        const errorMessages: string[] = [];
        const requiredMessage = " field is required.";
        if (this.state.firstName.trim().length === 0) {
            errorMessages.push("First Name" + requiredMessage);
        } else if (!this.isASCII(this.state.firstName.trim())) {
            errorMessages.push("First Name contains invalid character(s).");
        }
        if (this.state.lastName.trim().length === 0) {
            errorMessages.push("Last Name" + requiredMessage);
        } else if (!this.isASCII(this.state.lastName.trim())) {
            errorMessages.push("Last Name contains invalid character(s).");
        }
        if (this.state.email.trim().length === 0) {
            errorMessages.push("Email" + requiredMessage);
        } else if (!this.state.email.trim().match(Constants.EmailRegExp)) {
            errorMessages.push("Invalid Email Address");
        } else if (!this.isASCII(this.state.email.trim())) {
            errorMessages.push("Email Address contains invalid character(s).");
        }
        if (this.state.password.trim().length === 0) {
            errorMessages.push("Password" + requiredMessage);
        } else if (this.state.confirmPassword.trim().length === 0) {
            errorMessages.push("Confirm Password" + requiredMessage);
        } else if (this.state.password !== this.state.confirmPassword) {
            errorMessages.push("Provided passwords do not match.");
        } else if (!this.isASCII(this.state.password.trim())) {
            errorMessages.push("Password contains invalid character(s).");
        }
        if (this.state.phoneNumber.trim().length === 0) {
            errorMessages.push("Phone Number" + requiredMessage);
        }
        if (this.state.address1.trim().length === 0) {
            errorMessages.push("Address" + requiredMessage);
        } else if (
            !this.isASCII(this.state.address1.trim()) ||
            !this.isASCII(this.state.address2.trim())
        ) {
            errorMessages.push("Address contains invalid character(s).");
        }
        if (this.state.country.trim().length === 0) {
            errorMessages.push("Country" + requiredMessage);
        }
        if (this.state.province.trim().length === 0) {
            errorMessages.push("Province/State" + requiredMessage);
        }
        if (this.state.city.trim().length === 0) {
            errorMessages.push("Town/City" + requiredMessage);
        } else if (!this.isASCII(this.state.city.trim())) {
            errorMessages.push("Town/City contains invalid character(s).");
        }
        if (this.state.postalCode.trim().length === 0) {
            errorMessages.push("Postal Code/Zip" + requiredMessage);
        } else if (this.state.country === "CA" || this.state.country === "US") {
            if (
                !postcode.postcodeValidator(
                    this.state.postalCode,
                    this.state.country
                )
            ) {
                errorMessages.push("Postal Code/Zip is not valid.");
            }
        }
        const hasErrors = errorMessages.length > 0;
        this.setState({ hasErrors, errorMessages });

        if (hasErrors) {
            ReactGA.event(Constants.GAEvents.Form.Invalid);
            AppInsightsLogger.trackException({
                exception: new Error("Profile Update Validation Error"),
                properties: { errors: errorMessages }
            });
        }

        return !hasErrors;
    };

    private onSubmit = () => {
        this.setState({ hasErrors: false, errorMessages: [] });
        if (this.validateForm()) {
            ReactGA.event(Constants.GAEvents.Form.Submit);

            const model = {
                address1: this.state.address1,
                address2: this.state.address2,
                city: this.state.city,
                confirmPassword: this.state.confirmPassword,
                country: this.state.country,
                email: this.state.email,
                firstName: this.state.firstName,
                lastName: this.state.lastName,
                password: this.state.password,
                phoneNumber: this.state.phoneNumber,
                postalCode: this.state.postalCode,
                province: this.state.province
            } as IUserProfile;

            this.setState({ hasErrors: false, errorMessages: [] });
            this.props.updateUserProfile(model);
        }
    };
}

const reduxState = (state: IAppState) => ({
    ...state.userStoreState,
    ...state.appConfigStoreState
});
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(UserStore.actionCreators, dispatch);

export default withStyles(styles)(connect(reduxState, reduxDispatch)(Profile));
